import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';

import { normalizeGreekSearch } from '../../utils/greekUtils';
import PatientDetailDialog from '../PatientDetailDialog';

const MatchResultsTable = ({
  results = [],
  summary = {},
  onSelectCandidate,
  onPatientClick,
}) => {
  const { t } = useTranslation();
  const [filterStatus, setFilterStatus] = useState('ALL'); // 'ALL', 'EXACT', 'MULTIPLE', 'NOT_FOUND', 'FOUND'
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Candidate selection modal
  const [candidateModalItem, setCandidateModalItem] = useState(null);

  // Patient detail dialog state
  const [selectedPatientForDetail, setSelectedPatientForDetail] = useState(null);

  const filteredResults = useMemo(() => {
    return results.filter(item => {
      // 1. Status filter
      if (filterStatus === 'FOUND') {
        if (item.status === 'NOT_FOUND') return false;
      } else if (filterStatus === 'EXACT') {
        if (item.status !== 'EXACT_MATCH') return false;
      } else if (filterStatus === 'MULTIPLE') {
        if (item.status !== 'MULTIPLE_MATCHES') return false;
      } else if (filterStatus === 'NOT_FOUND') {
        if (item.status !== 'NOT_FOUND') return false;
      }

      // 2. Search term filter
      if (searchTerm) {
        const normTerm = normalizeGreekSearch(searchTerm);
        const searchPool = [
          item.rawName,
          item.rawLastName,
          item.rawFirstName,
          item.matchedPatient?.fullName,
          item.matchedPatient?.diagnosis,
          item.matchedPatient?.latestVisitDiagnosis,
          item.matchedPatient?.amka,
          item.status,
        ].filter(Boolean).map(v => normalizeGreekSearch(String(v)));

        return searchPool.some(val => val.includes(normTerm));
      }

      return true;
    });
  }, [results, filterStatus, searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderStatusChip = (status) => {
    switch (status) {
      case 'EXACT_MATCH':
        return (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: '15px !important' }} />}
            label={t('Exact Match')}
            size="small"
            color="success"
            sx={{ fontWeight: 700, fontSize: '0.75rem', height: 24 }}
          />
        );
      case 'MULTIPLE_MATCHES':
        return (
          <Chip
            icon={<HelpOutlineIcon sx={{ fontSize: '15px !important' }} />}
            label={t('Multiple Matches')}
            size="small"
            color="warning"
            sx={{ fontWeight: 700, fontSize: '0.75rem', height: 24 }}
          />
        );
      case 'FUZZY_MATCH':
        return (
          <Chip
            icon={<HelpOutlineIcon sx={{ fontSize: '15px !important' }} />}
            label={t('Approximate')}
            size="small"
            color="info"
            sx={{ fontWeight: 700, fontSize: '0.75rem', height: 24 }}
          />
        );
      case 'NOT_FOUND':
      default:
        return (
          <Chip
            icon={<CancelIcon sx={{ fontSize: '15px !important' }} />}
            label={t('Not Found')}
            size="small"
            color="error"
            variant="outlined"
            sx={{ fontWeight: 700, fontSize: '0.75rem', height: 24 }}
          />
        );
    }
  };

  const paginatedResults = filteredResults.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const matchedPercent = summary.total > 0
    ? Math.round(((summary.matched || 0) / summary.total) * 100)
    : 0;

  return (
    <Box>
      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card
            variant="outlined"
            onClick={() => setFilterStatus('ALL')}
            sx={{
              p: 2,
              borderRadius: 2.5,
              cursor: 'pointer',
              borderColor: filterStatus === 'ALL' ? 'primary.main' : 'divider',
              bgcolor: filterStatus === 'ALL' ? 'action.hover' : 'background.paper',
              transition: 'transform 0.15s ease',
              '&:hover': { transform: 'translateY(-2px)' },
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
              {t('Total Records')} {summary.total > 0 && `(${matchedPercent}% ${t('Matched')})`}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 900, color: 'text.primary', mt: 0.5 }}>
              {summary.total || 0}
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Card
            variant="outlined"
            onClick={() => setFilterStatus('EXACT')}
            sx={{
              p: 2,
              borderRadius: 2.5,
              cursor: 'pointer',
              borderColor: filterStatus === 'EXACT' ? 'success.main' : 'divider',
              bgcolor: filterStatus === 'EXACT' ? 'action.hover' : 'background.paper',
              transition: 'transform 0.15s ease',
              '&:hover': { transform: 'translateY(-2px)' },
            }}
          >
            <Typography variant="caption" color="success.main" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
              {t('Exact Matches')}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 900, color: 'success.main', mt: 0.5 }}>
              {summary.exact || 0}
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Card
            variant="outlined"
            onClick={() => setFilterStatus('MULTIPLE')}
            sx={{
              p: 2,
              borderRadius: 2.5,
              cursor: 'pointer',
              borderColor: filterStatus === 'MULTIPLE' ? 'warning.main' : 'divider',
              bgcolor: filterStatus === 'MULTIPLE' ? 'action.hover' : 'background.paper',
              transition: 'transform 0.15s ease',
              '&:hover': { transform: 'translateY(-2px)' },
            }}
          >
            <Typography variant="caption" color="warning.main" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
              {t('Multiple Candidates')}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 900, color: 'warning.main', mt: 0.5 }}>
              {summary.multiple || 0}
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Card
            variant="outlined"
            onClick={() => setFilterStatus('NOT_FOUND')}
            sx={{
              p: 2,
              borderRadius: 2.5,
              cursor: 'pointer',
              borderColor: filterStatus === 'NOT_FOUND' ? 'error.main' : 'divider',
              bgcolor: filterStatus === 'NOT_FOUND' ? 'action.hover' : 'background.paper',
              transition: 'transform 0.15s ease',
              '&:hover': { transform: 'translateY(-2px)' },
            }}
          >
            <Typography variant="caption" color="error.main" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
              {t('Not Found')}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 900, color: 'error.main', mt: 0.5 }}>
              {summary.notFound || 0}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Filter and Search Bar */}
      <Card
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2.5,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            <Chip
              label={`${t('All')} (${results.length})`}
              color={filterStatus === 'ALL' ? 'primary' : 'default'}
              variant={filterStatus === 'ALL' ? 'filled' : 'outlined'}
              onClick={() => setFilterStatus('ALL')}
              sx={{ fontWeight: 700 }}
            />
            <Chip
              label={`${t('Found')} (${(summary.matched || 0)})`}
              color={filterStatus === 'FOUND' ? 'success' : 'default'}
              variant={filterStatus === 'FOUND' ? 'filled' : 'outlined'}
              onClick={() => setFilterStatus('FOUND')}
              sx={{ fontWeight: 700 }}
            />
            <Chip
              label={`${t('Exact')} (${summary.exact || 0})`}
              color={filterStatus === 'EXACT' ? 'success' : 'default'}
              variant={filterStatus === 'EXACT' ? 'filled' : 'outlined'}
              onClick={() => setFilterStatus('EXACT')}
              sx={{ fontWeight: 700 }}
            />
            <Chip
              label={`${t('Multiple Candidates')} (${summary.multiple || 0})`}
              color={filterStatus === 'MULTIPLE' ? 'warning' : 'default'}
              variant={filterStatus === 'MULTIPLE' ? 'filled' : 'outlined'}
              onClick={() => setFilterStatus('MULTIPLE')}
              sx={{ fontWeight: 700 }}
            />
            <Chip
              label={`${t('Not Found')} (${summary.notFound || 0})`}
              color={filterStatus === 'NOT_FOUND' ? 'error' : 'default'}
              variant={filterStatus === 'NOT_FOUND' ? 'filled' : 'outlined'}
              onClick={() => setFilterStatus('NOT_FOUND')}
              sx={{ fontWeight: 700 }}
            />
          </Stack>

          <TextField
            size="small"
            placeholder={t('Search in results...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { xs: '100%', sm: 260 } }}
          />
        </Stack>
      </Card>

      {/* Main Results Table */}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          borderColor: 'divider',
        }}
      >
        <Table size="medium">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 800, width: 60 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 800, width: 140 }}>{t('Status')}</TableCell>
              <TableCell sx={{ fontWeight: 800, minWidth: 160 }}>{t('Imported Search Name')}</TableCell>
              <TableCell sx={{ fontWeight: 800, minWidth: 180 }}>{t('Matched Patient')}</TableCell>
              <TableCell sx={{ fontWeight: 800, minWidth: 220, bgcolor: 'primary.light', color: 'primary.dark' }}>
                {t('Primary Diagnosis')} 🩺
              </TableCell>
              <TableCell sx={{ fontWeight: 800, minWidth: 180 }}>{t('Latest Visit Diagnosis')}</TableCell>
              <TableCell sx={{ fontWeight: 800, width: 120 }}>{t('AMKA')}</TableCell>
              <TableCell sx={{ fontWeight: 800, width: 90 }}>{t('Age')}</TableCell>
              <TableCell sx={{ fontWeight: 800, width: 100 }} align="center">{t('Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedResults.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <Typography variant="body1" color="text.secondary">
                    {t('No records match the current filter.')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedResults.map((item) => {
                const p = item.matchedPatient;
                const hasMultiple = item.status === 'MULTIPLE_MATCHES' && item.candidates?.length > 1;

                return (
                  <TableRow
                    key={item.rowIndex}
                    hover
                    sx={{
                      bgcolor: item.status === 'NOT_FOUND' ? 'rgba(211, 47, 47, 0.02)' : 'inherit',
                      '&:last-child td, &:last-child th': { border: 0 },
                    }}
                  >
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      {item.rowIndex}
                    </TableCell>

                    <TableCell>
                      {renderStatusChip(item.status)}
                    </TableCell>

                    <TableCell sx={{ fontWeight: 700 }}>
                      {item.rawName || '—'}
                    </TableCell>

                    <TableCell>
                      {p ? (
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: 'primary.main',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              '&:hover': { textDecoration: 'underline' },
                            }}
                            onClick={() => setSelectedPatientForDetail(p)}
                          >
                            {p.fullName}
                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                          </Typography>

                          {hasMultiple && (
                            <Box sx={{ mt: 0.5 }}>
                              <Button
                                size="small"
                                variant="text"
                                color="warning"
                                startIcon={<SwapHorizIcon sx={{ fontSize: 14 }} />}
                                onClick={() => setCandidateModalItem(item)}
                                sx={{
                                  p: 0,
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  textTransform: 'none',
                                }}
                              >
                                {t('Change Match ({{count}} found)', { count: item.candidates.length })}
                              </Button>
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ bgcolor: p?.diagnosis ? 'rgba(25, 118, 210, 0.04)' : 'inherit' }}>
                      {p?.diagnosis ? (
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                          {p.diagnosis}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                          {p ? t('No diagnosis recorded') : '—'}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {p?.latestVisitDiagnosis ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {p.latestVisitDiagnosis}
                          </Typography>
                          {p.latestVisitDate && (
                            <Typography variant="caption" color="text.secondary">
                              {new Date(p.latestVisitDate).toLocaleDateString('el-GR')}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {p?.amka || '—'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {p?.age !== null && p?.age !== undefined ? p.age : '—'}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      {p && (
                        <Tooltip title={t('View Full Patient Details')}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => setSelectedPatientForDetail(p)}
                          >
                            <MedicalServicesIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredResults.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('Rows per page:')}
        />
      </TableContainer>

      {/* Candidate Disambiguation Modal */}
      {candidateModalItem && (
        <Dialog
          open={Boolean(candidateModalItem)}
          onClose={() => setCandidateModalItem(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            {t('Select Matching Patient')}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('Multiple patient records match "{{name}}". Choose the correct patient below:', {
                name: candidateModalItem.rawName,
              })}
            </Typography>

            <List sx={{ pt: 0 }}>
              {candidateModalItem.candidates.map((cand) => {
                const isSelected = candidateModalItem.matchedPatient?.id === cand.id;

                return (
                  <ListItem
                    key={cand.id}
                    disablePadding
                    sx={{
                      mb: 1,
                      border: '1px solid',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      borderRadius: 2,
                      bgcolor: isSelected ? 'action.hover' : 'background.paper',
                    }}
                  >
                    <ListItemButton
                      onClick={() => {
                        onSelectCandidate(candidateModalItem.rowIndex, cand);
                        setCandidateModalItem(null);
                      }}
                      sx={{ borderRadius: 2 }}
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {cand.fullName}
                            </Typography>
                            {isSelected && (
                              <Chip label={t('Selected')} size="small" color="primary" sx={{ height: 20 }} />
                            )}
                          </Stack>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" component="div" sx={{ fontWeight: 600, color: 'text.primary' }}>
                              🩺 {t('Diagnosis')}: {cand.diagnosis || t('None recorded')}
                            </Typography>
                            <Typography variant="caption" component="div" color="text.secondary">
                              AMKA: {cand.amka || '—'} | {t('Age')}: {cand.age || '—'} | {t('Phone')}: {cand.phone || '—'}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setCandidateModalItem(null)} sx={{ textTransform: 'none' }}>
              {t('Close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Patient Detail Modal */}
      {selectedPatientForDetail && (
        <PatientDetailDialog
          open={Boolean(selectedPatientForDetail)}
          patient={selectedPatientForDetail}
          onClose={() => setSelectedPatientForDetail(null)}
        />
      )}
    </Box>
  );
};

export default MatchResultsTable;
